import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/axios";
import UserAddresses from "./UserAddresses";
import { toast } from 'react-toastify';
import { useI18n } from "../../i18n/I18nContext";

export default function ProfilePage() {
  const { t } = useI18n();
  const [loading, setLoading] = useState(true);

  const [profile, setProfile] = useState({
    name: "",
    email: "",
    tax_number: "",
  });

  const [passwordData, setPasswordData] = useState({
    current_password: "",
    password: "",
    password_confirmation: "",
  });

  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  const [profileMessage, setProfileMessage] = useState("");
  const [passwordMessage, setPasswordMessage] = useState("");
  const [profileError, setProfileError] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const [emailVerified, setEmailVerified] = useState(false);
  const [sendingVerification, setSendingVerification] = useState(false);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        setLoading(true);

        const res = await api.get("/api/v1/profile");
        const data = res.data?.data;

        setProfile({
          name: data?.name || "",
          email: data?.email || "",
          tax_number: data?.tax_number || "",
        });

        setEmailVerified(!!data?.email_verified_at);
      } catch (error) {
        console.error("Erro ao carregar perfil:", error);
        setProfileError(t("Não foi possível carregar o perfil."));
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, []);

  const handleProfileChange = (field) => (e) => {
    setProfile((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const handlePasswordChange = (field) => (e) => {
    setPasswordData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setProfileMessage("");
    setProfileError("");
    setSavingProfile(true);

    try {
      const payload = {
        name: profile.name.trim(),
        email: profile.email.trim(),
        tax_number: profile.tax_number.trim() || null,
      };

      const res = await api.put("/api/v1/profile", payload);
      const data = res.data?.data;

      setProfile({
        name: data?.name || "",
        email: data?.email || "",
        tax_number: data?.tax_number || "",
      });

      try {
        const localUser = JSON.parse(localStorage.getItem("user") || "null");
        if (localUser) {
          localStorage.setItem(
            "user",
            JSON.stringify({
              ...localUser,
              name: data?.name || "",
              email: data?.email || "",
              tax_number: data?.tax_number || "",
            })
          );
        }
      } catch { }

      setProfileMessage(t("Perfil atualizado com sucesso."));
    } catch (error) {
      const data = error?.response?.data;

      if (data?.errors) {
        const firstKey = Object.keys(data.errors)[0];
        setProfileError(data.errors[firstKey]?.[0] || t("Erro de validação."));
      } else {
        setProfileError(data?.message || t("Não foi possível atualizar o perfil."));
      }
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordMessage("");
    setPasswordError("");
    setSavingPassword(true);

    try {
      await api.put("/api/v1/profile/password", {
        current_password: passwordData.current_password,
        password: passwordData.password,
        password_confirmation: passwordData.password_confirmation,
      });

      setPasswordData({
        current_password: "",
        password: "",
        password_confirmation: "",
      });

      setPasswordMessage(t("Palavra-passe atualizada com sucesso."));
    } catch (error) {
      const data = error?.response?.data;

      if (data?.errors) {
        const firstKey = Object.keys(data.errors)[0];
        setPasswordError(data.errors[firstKey]?.[0] || t("Erro de validação."));
      } else {
        setPasswordError(
          data?.message || t("Não foi possível atualizar a palavra-passe.")
        );
      }
    } finally {
      setSavingPassword(false);
    }
  };

  const handleResendVerification = async () => {
    if (emailVerified) {
      toast.info(t("O teu email já está verificado."), {
        position: "top-right",
      });
      return;
    }

    setSendingVerification(true);
    try {
      await api.post("/api/v1/email/verification-notification");
      toast.success(t("Email de verificação enviado!"), {
        position: "top-right",
      });
    } catch (err) {
      toast.error(t("Erro ao enviar o email de verificação."), {
        position: "top-right",
      });
    } finally {
      setSendingVerification(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-10 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-8 w-48 rounded bg-gray-200" />
            <div className="h-64 rounded-2xl bg-white shadow-sm" />
            <div className="h-72 rounded-2xl bg-white shadow-sm" />
            <div className="h-80 rounded-2xl bg-white shadow-sm" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-6">
      <div className="max-w-5xl mx-auto">
        <div className="mb-8">
          <p className="text-sm text-gray-500 mb-2">{t("A minha conta")}</p>
          <h1 className="text-3xl font-bold text-gray-900">{t("Perfil")}</h1>
          <p className="text-gray-600 mt-2">
            {t("Gere os teus dados pessoais, segurança e moradas guardadas.")}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-6">
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-gray-900">
                  {t("Dados pessoais")}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {t("Atualiza o teu nome, email e NIF.")}
                </p>
              </div>

              {profileMessage && (
                <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
                  {profileMessage}
                </div>
              )}

              {profileError && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {profileError}
                </div>
              )}

              <form onSubmit={handleProfileSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("Nome")}
                  </label>
                  <input
                    type="text"
                    value={profile.name}
                    onChange={handleProfileChange("name")}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("Email")}
                  </label>
                  <input
                    type="email"
                    value={profile.email}
                    onChange={handleProfileChange("email")}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("NIF")}
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={9}
                    value={profile.tax_number}
                    onChange={(e) =>
                      setProfile((prev) => ({
                        ...prev,
                        tax_number: e.target.value.replace(/\D/g, "").slice(0, 9),
                      }))
                    }
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="123456789"
                  />
                </div>

                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={savingProfile}
                    className="rounded-lg bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
                  >
                    {savingProfile ? t("A guardar...") : t("Guardar alterações")}
                  </button>
                </div>
              </form>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-6">
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-gray-900">
                  {t("Segurança")}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {t("Altera a tua palavra-passe.")}
                </p>
              </div>

              {passwordMessage && (
                <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
                  {passwordMessage}
                </div>
              )}

              {passwordError && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {passwordError}
                </div>
              )}

              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("Palavra-passe atual")}
                  </label>
                  <input
                    type="password"
                    value={passwordData.current_password}
                    onChange={handlePasswordChange("current_password")}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("Nova palavra-passe")}
                  </label>
                  <input
                    type="password"
                    value={passwordData.password}
                    onChange={handlePasswordChange("password")}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("Confirmar nova palavra-passe")}
                  </label>
                  <input
                    type="password"
                    value={passwordData.password_confirmation}
                    onChange={handlePasswordChange("password_confirmation")}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                </div>

                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={savingPassword}
                    className="rounded-lg bg-gray-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-gray-800 disabled:opacity-60"
                  >
                    {savingPassword
                      ? t("A atualizar...")
                      : t("Atualizar palavra-passe")}
                  </button>
                </div>
              </form>
            </div>

            <UserAddresses />
          </div>

          <div className="space-y-6">
            <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">
                {t("Resumo da conta")}
              </h2>

              <div className="space-y-3 text-sm text-gray-600">
                <div>
                  <span className="block text-gray-500">{t("Nome")}</span>
                  <span className="font-medium text-gray-900">
                    {profile.name || "—"}
                  </span>
                </div>

                <div>
                  <span className="block text-gray-500">{t("Email")}</span>
                  <span className="font-medium text-gray-900">
                    {profile.email || "—"}
                  </span>
                </div>

                <div>
                  <span className="block text-gray-500">{t("NIF")}</span>
                  <span className="font-medium text-gray-900">
                    {profile.tax_number || t("Não definido")}
                  </span>
                </div>
                <div>
                  <span className="block text-gray-500">{t("Email verificado")}</span>
                  <span className={`font-medium ${emailVerified ? "text-green-600" : "text-amber-600"}`}>
                    {emailVerified ? t("✓ Verificado") : t("✗ Não verificado")}
                  </span>
                </div>

                {!emailVerified && (
                  <div className="pt-2 border-t border-gray-100">
                    <button
                      onClick={handleResendVerification}
                      disabled={sendingVerification}
                      className="w-full rounded-lg border border-amber-300 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-700 hover:bg-amber-100 disabled:opacity-60 transition"
                    >
                      {sendingVerification ? t("A enviar...") : t("Reenviar email de verificação")}
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">
                {t("Encomendas")}
              </h2>

              <p className="text-sm text-gray-600 mb-4">
                {t("Consulta o histórico completo das tuas encomendas na área dedicada.")}
              </p>

              <Link
                to="/orders"
                className="inline-flex rounded-lg bg-white border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50"
              >
                {t("Ver encomendas")}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}